// LOOP_CHECKOUT_PREMIUM_UX_V3
// LOOP_CHECKOUT_PREMIUM_UX_V2
// LOOP_CHECKOUT_PREMIUM_UX_V1
// src/pages/MembershipSubscriptionCheckoutPage.jsx
// LOOP Membership Subscriptions V2C — Wompi Panama Sandbox PREAUTHORIZATION + CAPTURE handshake.
// Sandbox only: preauthorization owns initial 3DS; capture activates benefits only after APPROVED.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  BadgeCheck,
  CalendarDays,
  CheckCircle2,
  ChevronRight,
  CreditCard,
  Crown,
  ExternalLink,
  FlaskConical,
  Loader2,
  Lock,
  LogIn,
  RefreshCw,
  ShieldCheck,
  Store,
  Ticket,
  AlertTriangle,
} from "lucide-react";
import { callCallableNativeSafe } from "../utils/callableNativeSafe";
import {
  LOOP_LEGAL_PAYMENTS_VERSION,
  LOOP_MEMBERSHIP_CANCELLATION_POLICY_VERSION,
  LOOP_MEMBERSHIP_SUBSCRIPTION_TERMS_VERSION,
} from "../utils/legalVersions";
import WompiProductionPaymentCard from "../components/memberships/WompiProductionPaymentCard";

const WOMPI_SANDBOX_API_BASE = "https://api-sandbox.wompi.pa/v1";
const MASTERCARD_ID_CHECK_LOGO = "https://docs.wompi.co/img/mc-id-check-logo.svg";

const SANDBOX_CARD_SCENARIOS = [
  {
    id: "browser-approved",
    label: "Aprobada · BROWSER_INFO",
    detail: "Prueba aprobación con autenticación sin fricción.",
    number: "4917480000000057",
  },
  {
    id: "challenge-approved",
    label: "Aprobada · CHALLENGE",
    detail: "Prueba el reto 3DS interactivo.",
    number: "4970110000001029",
  },
  {
    id: "browser-declined",
    label: "Rechazada · BROWSER_INFO",
    detail: "Prueba el manejo de una transacción rechazada.",
    number: "4917480000000008",
  },
];

function money(value, currency = "USD") {
  const amount = Number(value || 0);
  try {
    return new Intl.NumberFormat("es-PA", {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 2,
    }).format(Number.isFinite(amount) ? amount : 0);
  } catch (_) {
    return `$${Number.isFinite(amount) ? amount.toFixed(2) : "0.00"}`;
  }
}

function cycleLabel(value) {
  const cycle = String(value || "monthly").toLowerCase();
  if (cycle === "weekly") return "semana";
  if (cycle === "quarterly") return "trimestre";
  if (cycle === "yearly") return "año";
  return "mes";
}

function benefitLabel(plan = {}) {
  const type = String(plan.benefitType || plan.membershipType || "credits").toLowerCase();
  if (type === "unlimited") return "Uso ilimitado";
  if (type === "discount") {
    const discount = Number(plan.discountPercent || 0);
    return discount > 0 ? `${discount}% descuento` : "Descuento del plan";
  }
  const credits = Number(plan.includedCredits || 0);
  return `${credits} ${plan.creditUnit || "servicios"}`;
}

function idempotencyKey(businessId, planId) {
  const storageKey = `loop_membership_draft_key_${businessId}_${planId}`;
  try {
    const existing = window.sessionStorage.getItem(storageKey);
    if (existing) return existing;
    const generated = window.crypto?.randomUUID
      ? window.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    window.sessionStorage.setItem(storageKey, generated);
    return generated;
  } catch (_) {
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

function newPaymentAttemptKey() {
  try {
    return window.crypto?.randomUUID
      ? window.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  } catch (_) {
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

function errorMessage(error) {
  const raw = String(error?.code || error?.message || error || "").toLowerCase();
  if (raw.includes("app-check") || raw.includes("app check")) {
    return "No pudimos validar este dispositivo. Reabre Loop e inténtalo nuevamente.";
  }
  if (raw.includes("unauthenticated")) return "Inicia sesión nuevamente para continuar.";
  if (raw.includes("failed-precondition")) return error?.message || "Este plan todavía no está disponible.";
  if (raw.includes("unavailable") || raw.includes("deadline")) {
    return "Wompi Sandbox tardó en responder. Puedes reintentar el mismo intento.";
  }
  return error?.message || "No pudimos continuar con la suscripción.";
}

function isRetryableNetworkError(error) {
  const raw = String(error?.code || error?.message || error || "").toLowerCase();
  return raw.includes("unavailable") || raw.includes("deadline") || raw.includes("timeout");
}

function isAppCheckFailure(error) {
  let details = "";
  try {
    details = JSON.stringify(error?.details || error?.customData || {});
  } catch (_) {}

  const raw = [
    error?.code,
    error?.message,
    details,
    error,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return (
    raw.includes("app check") ||
    raw.includes("app-check") ||
    raw.includes("appcheck") ||
    raw.includes("firebase-app-check") ||
    raw.includes("app-check-token-missing") ||
    raw.includes("app-check-token-unavailable") ||
    raw.includes("debug-token-not-registered")
  );
}

function isUnauthenticatedError(error) {
  if (isAppCheckFailure(error)) return false;

  const raw = String(error?.code || error?.message || error || "").toLowerCase();
  return raw.includes("unauthenticated") || raw.includes("auth/unauthenticated");
}

function currentCheckoutPath(businessId, planId = "") {
  const businessPart = encodeURIComponent(String(businessId || "").trim());
  const planPart = String(planId || "").trim();
  return planPart
    ? `/membership-checkout/${businessPart}/${encodeURIComponent(planPart)}`
    : `/membership-checkout/${businessPart}`;
}

function loginPathFor(returnTo) {
  return `/login?from=${encodeURIComponent(returnTo)}`;
}

function decodeThreeDsHtml(value) {
  const raw = String(value || "");
  if (!raw) return "";
  if (!/&(?:lt|gt|quot|amp|#\d+|#x[a-f0-9]+);/i.test(raw)) return raw;
  try {
    const doc = new DOMParser().parseFromString(raw, "text/html");
    return doc.documentElement?.textContent || raw;
  } catch (_) {
    return raw;
  }
}

function sandboxExpirationYear() {
  return String((new Date().getFullYear() + 3) % 100).padStart(2, "0");
}

function assertSandboxSetup(setup) {
  const publicKey = String(setup?.publicKey || "");
  const apiBase = String(setup?.apiBase || "");
  const tokenizationUrl = String(setup?.tokenizationUrl || "");
  if (
    !publicKey.startsWith("pub_test_") ||
    apiBase !== WOMPI_SANDBOX_API_BASE ||
    tokenizationUrl !== `${WOMPI_SANDBOX_API_BASE}/tokens/cards`
  ) {
    throw new Error("La configuración recibida no corresponde a Wompi Sandbox.");
  }
}

async function tokenizeSandboxScenario(setup, scenario) {
  assertSandboxSetup(setup);
  if (!scenario || !SANDBOX_CARD_SCENARIOS.some((item) => item.id === scenario.id)) {
    throw new Error("Escenario Sandbox inválido.");
  }

  const response = await fetch(setup.tokenizationUrl, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${setup.publicKey}`,
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({
      number: scenario.number,
      cvc: "123",
      exp_month: "12",
      exp_year: sandboxExpirationYear(),
      card_holder: "Loop Sandbox",
    }),
  });

  let body = null;
  try {
    body = await response.json();
  } catch (_) {
    body = null;
  }

  const token = String(body?.data?.id || "").trim();
  if (!response.ok || body?.status !== "CREATED" || !token.startsWith("tok_")) {
    throw new Error("Wompi Sandbox no pudo tokenizar la tarjeta de prueba.");
  }

  return {
    id: token,
    brand: String(body?.data?.brand || ""),
    lastFour: String(body?.data?.last_four || ""),
  };
}

function PlanCard({ plan, onSelect }) {
  return (
    <button type="button" className="msc-plan-card" onClick={() => onSelect(plan)}>
      <span className="msc-plan-icon"><Crown size={22} /></span>
      <span className="msc-plan-copy">
        <strong>{plan.name || "Membresía"}</strong>
        <small>{plan.description || "Beneficios recurrentes del negocio."}</small>
        <em><Ticket size={14} /> {benefitLabel(plan)}</em>
      </span>
      <span className="msc-plan-price">
        <strong>{money(plan.price, plan.currency)}</strong>
        <small>por {cycleLabel(plan.billingCycle)}</small>
      </span>
      <ChevronRight size={18} />
    </button>
  );
}

function SandboxPaymentCard({
  subscription,
  setup,
  setupLoading,
  setupError,
  onLoadSetup,
  onSubscription,
}) {
  const [wompiAccepted, setWompiAccepted] = useState(false);
  const [busyScenario, setBusyScenario] = useState("");
  const [error, setError] = useState("");
  const [preauthorization, setPreauthorization] = useState(null);
  const [transaction, setTransaction] = useState(null);
  const [syncingActive, setSyncingActive] = useState(false);
  const attemptRef = useRef(null);
  const pollTimerRef = useRef(null);

  const status = String(subscription?.status || "");
  const paymentFlowVersion = String(subscription?.paymentFlowVersion || "");
  const providerStage = String(subscription?.payment?.providerStage || "");
  const preauthorizationStatus = String(
    subscription?.payment?.preauthorizationStatus ||
      subscription?.wompiSandboxPaymentSourceStatus ||
      "",
  ).toUpperCase();
  const providerPending = status === "pending_payment";
  const approvedPendingActivation = status === "pending_activation";
  const activeMembership = status === "active";
  const canStart = status === "draft" && setup && !setupLoading;

  const threeDs = transaction?.threeDs || preauthorization?.threeDs || null;
  const threeDsOrigin = transaction?.threeDs
    ? "CAPTURE"
    : preauthorization?.threeDs
      ? "PREAUTHORIZATION"
      : "";
  const threeDsStep = String(threeDs?.currentStep || "").toUpperCase();
  const threeDsStatus = String(threeDs?.currentStepStatus || "").toUpperCase();
  const threeDsHtml = useMemo(
    () => decodeThreeDsHtml(threeDs?.methodData || ""),
    [threeDs?.methodData],
  );
  const hasThreeDs = Boolean(threeDs);
  const visibleChallenge =
    threeDsStep === "CHALLENGE" &&
    threeDsStatus !== "COMPLETED" &&
    Boolean(threeDsHtml);
  const hiddenThreeDs =
    ["BROWSER_INFO", "FINGERPRINT"].includes(threeDsStep) &&
    threeDsStatus !== "COMPLETED" &&
    Boolean(threeDsHtml);

  const poll = async () => {
    if (!subscription?.id) return false;
    if (activeMembership) setSyncingActive(true);
    try {
      const result = await callCallableNativeSafe({
        functionName: "getMembershipWompiSandboxPaymentStatus",
        data: { subscriptionId: subscription.id },
        requireAuth: true,
        requireAppCheck: true,
        timeoutMs: 45000,
      });
      const nextSubscription = result?.data?.subscription || null;
      const nextPreauthorization = result?.data?.preauthorization || null;
      const nextTransaction = result?.data?.transaction || null;
      if (nextSubscription) onSubscription(nextSubscription);
      if (nextPreauthorization) setPreauthorization(nextPreauthorization);
      if (nextTransaction) setTransaction(nextTransaction);
      const nextStatus = String(nextSubscription?.status || "");
      const shouldContinue = nextStatus === "pending_payment";
      if (!shouldContinue) {
        attemptRef.current = null;
      }
      return shouldContinue;
    } catch (pollError) {
      console.error("[MembershipSubscriptionCheckoutPage] sandbox poll failed", pollError);
      setError(errorMessage(pollError));
      return true;
    } finally {
      if (activeMembership) setSyncingActive(false);
    }
  };

  useEffect(() => {
    if (!providerPending || !subscription?.id) return undefined;

    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      const shouldContinue = await poll();
      if (!cancelled && shouldContinue) {
        pollTimerRef.current = window.setTimeout(tick, 3000);
      }
    };

    tick();
    return () => {
      cancelled = true;
      if (pollTimerRef.current) {
        window.clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [providerPending, subscription?.id]);

  const startScenario = async (scenario) => {
    if (!subscription?.id || !setup || !wompiAccepted || busyScenario) return;
    setBusyScenario(scenario.id);
    setError("");

    let attempt = attemptRef.current;
    try {
      if (!attempt || attempt.scenarioId !== scenario.id) {
        const token = await tokenizeSandboxScenario(setup, scenario);
        attempt = {
          scenarioId: scenario.id,
          idempotencyKey: newPaymentAttemptKey(),
          cardToken: token.id,
        };
        attemptRef.current = attempt;
      }

      const result = await callCallableNativeSafe({
        functionName: "startMembershipWompiSandboxPayment",
        data: {
          subscriptionId: subscription.id,
          idempotencyKey: attempt.idempotencyKey,
          cardToken: attempt.cardToken,
          wompiAcceptanceAccepted: true,
        },
        requireAuth: true,
        requireAppCheck: true,
        timeoutMs: 60000,
      });

      const nextSubscription = result?.data?.subscription || null;
      const nextPreauthorization = result?.data?.preauthorization || null;
      const nextTransaction = result?.data?.transaction || null;
      if (nextSubscription) onSubscription(nextSubscription);
      if (nextPreauthorization) setPreauthorization(nextPreauthorization);
      if (nextTransaction) setTransaction(nextTransaction);

      if (nextSubscription?.status !== "pending_payment") {
        attemptRef.current = null;
      }
    } catch (paymentError) {
      console.error("[MembershipSubscriptionCheckoutPage] sandbox payment failed", paymentError);
      setError(errorMessage(paymentError));
      if (!isRetryableNetworkError(paymentError)) {
        attemptRef.current = null;
      }
    } finally {
      setBusyScenario("");
    }
  };

  return (
    <div className="msc-sandbox-card">
      <div className="msc-sandbox-title">
        <span className="msc-sandbox-icon"><FlaskConical size={18} /></span>
        <div>
          <span>Wompi Sandbox</span>
          <strong>Prueba técnica de pago recurrente</strong>
          <small>No acepta tarjetas reales. Los botones usan exclusivamente datos oficiales de prueba de Wompi.</small>
        </div>
      </div>

      {setupLoading ? (
        <div className="msc-sandbox-state"><Loader2 className="msc-spin" size={17} /> Preparando Wompi Sandbox...</div>
      ) : null}

      {setupError ? (
        <div className="msc-sandbox-error">
          <AlertTriangle size={17} />
          <span>{setupError}</span>
          <button type="button" onClick={onLoadSetup}>Reintentar</button>
        </div>
      ) : null}

      {error ? <div className="msc-sandbox-error"><AlertTriangle size={17} /> {error}</div> : null}

      {setup ? (
        <label className="msc-wompi-consent">
          <input
            type="checkbox"
            checked={wompiAccepted}
            onChange={(event) => setWompiAccepted(event.target.checked)}
            disabled={providerPending || approvedPendingActivation}
          />
          <span>
            <strong>Tratamiento de datos Wompi</strong>
            Confirmo que revisé y acepto los términos vigentes de Wompi para esta prueba.
            {setup?.acceptance?.permalink ? (
              <a
                href={setup.acceptance.permalink}
                target="_blank"
                rel="noreferrer"
                onClick={(event) => event.stopPropagation()}
              >
                Abrir términos <ExternalLink size={12} />
              </a>
            ) : null}
          </span>
        </label>
      ) : null}

      {canStart ? (
        <div className="msc-sandbox-scenarios">
          {SANDBOX_CARD_SCENARIOS.map((scenario) => (
            <button
              type="button"
              key={scenario.id}
              onClick={() => startScenario(scenario)}
              disabled={!wompiAccepted || Boolean(busyScenario)}
            >
              {busyScenario === scenario.id ? <Loader2 className="msc-spin" size={16} /> : <CreditCard size={16} />}
              <span><strong>{scenario.label}</strong><small>{scenario.detail}</small></span>
            </button>
          ))}
        </div>
      ) : null}

      {providerPending ? (
        <div className="msc-payment-pending">
          <Loader2 className="msc-spin" size={19} />
          <div>
            <strong>
              {providerStage === "preauthorization"
                ? "Autenticando método de pago Sandbox"
                : "Confirmando captura inicial Sandbox"}
            </strong>
            <span>
              {providerStage === "preauthorization"
                ? `Preautorización ${preauthorizationStatus || "PROCESSING"}. Wompi puede requerir 3D Secure antes de habilitar el cobro recurrente.`
                : "La fuente ya fue preautorizada. Estamos confirmando una única captura inicial antes de activar la membresía."}
            </span>
          </div>
          <button type="button" onClick={poll}>Consultar ahora</button>
        </div>
      ) : null}

      {approvedPendingActivation ? (
        <div className="msc-payment-approved">
          <CheckCircle2 size={20} />
          <div>
            <strong>Pago Sandbox aprobado</strong>
            <span>El pago ya está confirmado. Valida una sola vez el puente UID de Phase 2B usando la misma transacción.</span>
          </div>
          <button type="button" onClick={poll}>Validar activación UID</button>
        </div>
      ) : null}

      {activeMembership ? (
        <>
          <div className="msc-payment-approved">
            <CheckCircle2 size={20} />
            <div>
              <strong>Membresía Sandbox activa</strong>
              <span>El capture inicial fue aprobado y el beneficio quedó activo. Este botón reconsulta la misma transacción sin iniciar otro cobro.</span>
            </div>
            <button type="button" onClick={poll} disabled={syncingActive}>
              {syncingActive ? "Sincronizando..." : "Reconsultar pago aprobado"}
            </button>
          </div>

          <div className="msc-ledger-inspector">
            <strong>Diagnóstico local · payment ledger</strong>
            <div><span>Flujo</span><code>{paymentFlowVersion || "legacy"}</code></div>
            <div><span>Provider stage</span><code>{providerStage || "—"}</code></div>
            <div><span>Preauth status</span><code>{preauthorizationStatus || "—"}</code></div>
            <div><span>Loop paymentCode</span><code>{subscription?.payment?.paymentCode || "—"}</code></div>
            <div><span>Wompi transaction</span><code>{subscription?.payment?.providerTransactionId || subscription?.payment?.transactionId || "—"}</code></div>
            <div><span>Loop/Wompi reference</span><code>{subscription?.payment?.reference || "—"}</code></div>
            <div><span>Ciclo</span><code>{subscription?.payment?.cycleNumber ?? "—"}</code></div>
            <div><span>Intento del ciclo</span><code>{subscription?.payment?.attemptNumber ?? "—"}</code></div>
            <div><span>Intento global</span><code>{subscription?.payment?.globalAttemptNumber ?? "—"}</code></div>
          </div>
        </>
      ) : null}

      {hasThreeDs ? (
        <div className="msc-three-ds">
          <div className="msc-three-ds-head">
            <img src={MASTERCARD_ID_CHECK_LOGO} alt="Mastercard ID Check" />
            <div>
              <strong>Autenticación 3D Secure</strong>
              <span>{threeDsOrigin ? `${threeDsOrigin} · ` : ""}{threeDsStep || "PROCESANDO"} · {threeDsStatus || "PENDING"}</span>
            </div>
          </div>
          {hiddenThreeDs ? (
            <iframe
              title={`Wompi 3DS ${threeDsStep}`}
              className="msc-3ds-hidden"
              srcDoc={threeDsHtml}
            />
          ) : null}
          {visibleChallenge ? (
            <iframe
              title="Wompi 3DS Challenge"
              className="msc-3ds-challenge"
              srcDoc={threeDsHtml}
            />
          ) : null}
        </div>
      ) : null}

      <div className="msc-sandbox-foot">
        <Lock size={15} />
        <span>Loop recibe solamente el token temporal de Wompi; este checkout Sandbox no envía números de tarjeta al backend de Loop.</span>
      </div>
    </div>
  );
}

function MobileCheckoutProgress({ step, label, title, copy }) {
  return (
    <div className="msc-mobile-progress">
      <div className="msc-mobile-progress-top">
        <span>{step} de 3</span>
        <strong>{label}</strong>
      </div>
      <div className="msc-mobile-progress-bars" aria-hidden="true">
        {[1, 2, 3].map((item) => (
          <i
            key={item}
            className={item <= step ? "is-active" : ""}
          />
        ))}
      </div>
      <h2>{title}</h2>
      {copy ? <p>{copy}</p> : null}
    </div>
  );
}

export default function MembershipSubscriptionCheckoutPage() {
  const navigate = useNavigate();
  const { businessId = "", planId = "" } = useParams();
  const [business, setBusiness] = useState(null);
  const [plans, setPlans] = useState([]);
  const [preview, setPreview] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [needsAuth, setNeedsAuth] = useState(false);
  const [sandboxSetup, setSandboxSetup] = useState(null);
  const [sandboxSetupLoading, setSandboxSetupLoading] = useState(false);
  const [sandboxSetupError, setSandboxSetupError] = useState("");
  const [productionPaymentAvailable, setProductionPaymentAvailable] = useState(null);
  const [productionAvailabilitySubscriptionId, setProductionAvailabilitySubscriptionId] = useState("");
  const [productionAvailabilityError, setProductionAvailabilityError] = useState("");
  const [productionAvailabilityAttempt, setProductionAvailabilityAttempt] = useState(0);
  const [consents, setConsents] = useState({
    recurring: false,
    payments: false,
    terms: false,
    cancellation: false,
  });
  const [mobileStep, setMobileStep] = useState(1);
  const [mobileDescriptionOpen, setMobileDescriptionOpen] = useState(false);
  const [expandedConsent, setExpandedConsent] = useState("");

  const allAccepted = useMemo(
    () => Object.values(consents).every(Boolean),
    [consents],
  );

  const goToMobileStep = (step) => {
    setMobileStep(step);
    setExpandedConsent("");
    if (typeof window !== "undefined") {
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      });
    }
  };

  const legacyProductionPreviewFlag = preview?.purchaseTerms?.productionPaymentAvailable === true;
  const productionAvailabilityResolved =
    Boolean(subscription?.id) &&
    productionAvailabilitySubscriptionId === subscription.id &&
    typeof productionPaymentAvailable === "boolean";

  /*
   * Once a Production payment session has started, availability discovery
   * is no longer the routing authority for this subscription.
   *
   * WompiProductionPaymentCard owns exact status polling, 3DS continuation,
   * terminal reconciliation and entitlement activation.
   */
  const productionPaymentSessionStarted =
    Boolean(subscription?.id) &&
    String(subscription?.paymentEnvironment || "").toLowerCase() === "production" &&
    (
      subscription?.checkout?.paymentStarted === true ||
      subscription?.payment?.initialAuthProviderPostStarted === true ||
      Boolean(
        String(
          subscription?.payment?.transactionId ||
            subscription?.payment?.providerTransactionId ||
            "",
        ).trim(),
      ) ||
      ["pending_payment", "pending_activation"].includes(
        String(subscription?.status || ""),
      )
    );

  const productionPaymentRouteReady =
    productionPaymentSessionStarted ||
    (productionAvailabilityResolved && productionPaymentAvailable);

  const load = async () => {
    setLoading(true);
    setError("");
    setNeedsAuth(false);
    try {
      if (planId) {
        const result = await callCallableNativeSafe({
          functionName: "getMembershipSubscriptionCheckoutPreview",
          data: { businessId, planId },
          requireAuth: true,
          requireAppCheck: true,
          timeoutMs: 45000,
        });
        setPreview(result?.data || null);
        setBusiness(result?.data?.business || null);
        setSubscription(result?.data?.existingSubscription || null);
      } else {
        const result = await callCallableNativeSafe({
          functionName: "getPublicBusinessMembershipPlans",
          data: { businessId },
          requireAuth: false,
          requireAppCheck: true,
          timeoutMs: 45000,
        });
        setBusiness(result?.data?.business || null);
        setPlans(Array.isArray(result?.data?.plans) ? result.data.plans : []);
      }
    } catch (loadError) {
      console.error("[MembershipSubscriptionCheckoutPage] load failed", loadError);
      if (isUnauthenticatedError(loadError)) {
        setNeedsAuth(true);
        setError("");
      } else {
        setError(errorMessage(loadError));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [businessId, planId]);

  useEffect(() => {
    setMobileStep(1);
    setMobileDescriptionOpen(false);
    setExpandedConsent("");
  }, [businessId, planId]);

  const selectPlan = (plan) => {
    navigate(`/membership-checkout/${encodeURIComponent(businessId)}/${encodeURIComponent(plan.planId)}`);
  };

  const goToLogin = () => {
    navigate(loginPathFor(currentCheckoutPath(businessId, planId)));
  };

  const createDraft = async () => {
    if (
      !preview?.checkoutEligible ||
      preview?.purchaseTerms?.paymentAvailable !== true ||
      !allAccepted ||
      saving
    ) return null;
    setSaving(true);
    setError("");
    try {
      const result = await callCallableNativeSafe({
        functionName: "createMembershipSubscriptionDraft",
        data: {
          businessId,
          planId,
          idempotencyKey: idempotencyKey(businessId, planId),
          legalConsent: {
            recurringChargeAccepted: consents.recurring,
            paymentsPolicyAccepted: consents.payments,
            termsAccepted: consents.terms,
            cancellationPolicyAccepted: consents.cancellation,
            paymentsPolicyVersion: LOOP_LEGAL_PAYMENTS_VERSION,
            subscriptionTermsVersion: LOOP_MEMBERSHIP_SUBSCRIPTION_TERMS_VERSION,
            cancellationPolicyVersion: LOOP_MEMBERSHIP_CANCELLATION_POLICY_VERSION,
            source: "membership_checkout_production_v1",
          },
        },
        requireAuth: true,
        requireAppCheck: true,
        timeoutMs: 45000,
      });
      const nextSubscription = result?.data?.subscription || null;
      setSubscription(nextSubscription);
      return nextSubscription;
    } catch (saveError) {
      console.error("[MembershipSubscriptionCheckoutPage] draft failed", saveError);
      setError(errorMessage(saveError));
      return null;
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const status = String(subscription?.status || "");
    if (
      !subscription?.id ||
      !["draft", "pending_payment", "pending_activation"].includes(status)
    ) {
      setProductionPaymentAvailable(null);
      setProductionAvailabilitySubscriptionId("");
      setProductionAvailabilityError("");
      return undefined;
    }

    if (productionPaymentSessionStarted) {
      setProductionAvailabilityError("");
      return undefined;
    }

    let cancelled = false;
    const subscriptionId = subscription.id;

    setProductionPaymentAvailable(null);
    setProductionAvailabilitySubscriptionId("");
    setProductionAvailabilityError("");

    callCallableNativeSafe({
      functionName: "getMembershipWompiProductionAvailability",
      data: { subscriptionId },
      requireAuth: true,
      requireAppCheck: true,
      timeoutMs: 20000,
    })
      .then((result) => {
        if (cancelled) return;
        const payload = result?.data || {};
        setProductionPaymentAvailable(payload.productionPaymentAvailable === true);
        setProductionAvailabilitySubscriptionId(subscriptionId);
        setProductionAvailabilityError("");
      })
      .catch((availabilityError) => {
        if (cancelled) return;
        console.error(
          "[MembershipSubscriptionCheckoutPage] production availability failed",
          availabilityError,
        );
        setProductionPaymentAvailable(null);
        setProductionAvailabilitySubscriptionId("");
        setProductionAvailabilityError(errorMessage(availabilityError));
      });

    return () => {
      cancelled = true;
    };
  // Do not re-run availability merely because payment status changes.
  // productionPaymentSessionStarted already captures the transition that
  // hands routing authority to WompiProductionPaymentCard.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    subscription?.id,
    productionPaymentSessionStarted,
    legacyProductionPreviewFlag,
    productionAvailabilityAttempt,
  ]);

  const loadSandboxSetup = async () => {
    if (!subscription?.id) return;
    setSandboxSetupLoading(true);
    setSandboxSetupError("");
    try {
      const result = await callCallableNativeSafe({
        functionName: "getMembershipWompiSandboxSetup",
        data: { subscriptionId: subscription.id },
        requireAuth: true,
        requireAppCheck: true,
        timeoutMs: 45000,
      });
      const setup = result?.data || null;
      assertSandboxSetup(setup);
      setSandboxSetup(setup);
      if (setup?.subscription) {
        setSubscription(setup.subscription);
      }
    } catch (setupError) {
      console.error("[MembershipSubscriptionCheckoutPage] sandbox setup failed", setupError);
      setSandboxSetup(null);
      setSandboxSetupError(errorMessage(setupError));
    } finally {
      setSandboxSetupLoading(false);
    }
  };

  useEffect(() => {
    const status = String(subscription?.status || "");
    if (
      !productionPaymentSessionStarted &&
      productionAvailabilityResolved &&
      !productionAvailabilityError &&
      !productionPaymentAvailable &&
      preview?.purchaseTerms?.sandboxPaymentAvailable === true &&
      subscription?.id &&
      ["draft", "pending_payment", "pending_activation"].includes(status)
    ) {
      loadSandboxSetup();
    } else {
      setSandboxSetup(null);
      setSandboxSetupError("");
    }
  }, [
    productionPaymentSessionStarted,
    productionAvailabilityResolved,
    productionAvailabilityError,
    productionPaymentAvailable,
    subscription?.id,
    subscription?.status,
    preview?.purchaseTerms?.sandboxPaymentAvailable,
  ]);

  const plan = preview?.plan || null;
  const existingOpen = subscription && !["cancelled", "expired"].includes(subscription.status);
  const paymentApprovedPendingActivation = subscription?.status === "pending_activation";
  const paymentPending = subscription?.status === "pending_payment";
  const membershipActive = subscription?.status === "active";
  const paymentFlowLocked =
    productionPaymentSessionStarted ||
    paymentPending ||
    paymentApprovedPendingActivation ||
    membershipActive;

  const consentItems = plan
    ? [
        {
          key: "recurring",
          title: "Renovación automática",
          short: `${money(plan.price, plan.currency)} cada mes hasta que canceles.`,
          detail: `Se cobrará ${money(plan.price, plan.currency)} cada mes hasta que canceles la renovación.`,
        },
        {
          key: "payments",
          title: "Política de pagos",
          short: "Cobros, renovaciones y reembolsos.",
          detail: "Aceptas la política vigente de pagos, renovaciones y reembolsos.",
        },
        {
          key: "terms",
          title: "Términos de suscripción",
          short: "Uso y beneficios de tu membresía.",
          detail: "Tus beneficios se activan después de un pago aprobado y se renuevan con cada ciclo pagado.",
        },
        {
          key: "cancellation",
          title: "Cancelación flexible",
          short: "Detén futuras renovaciones cuando quieras.",
          detail: "Puedes detener la renovación cuando quieras y conservar tus beneficios hasta el final del período pagado.",
        },
      ]
    : [];

  const setAllConsents = (accepted) => {
    setConsents({
      recurring: accepted,
      payments: accepted,
      terms: accepted,
      cancellation: accepted,
    });
  };

  const continueMobileToPayment = async () => {
    if (existingOpen) {
      goToMobileStep(3);
      return;
    }

    const nextSubscription = await createDraft();
    if (nextSubscription?.id) {
      goToMobileStep(3);
    }
  };

  useEffect(() => {
    if (!subscription?.id) return;

    if (
      productionPaymentSessionStarted ||
      paymentPending ||
      paymentApprovedPendingActivation ||
      membershipActive
    ) {
      setMobileStep(3);
      return;
    }

    if (existingOpen) {
      setMobileStep((current) => (current === 1 ? 3 : current));
    }
  }, [
    subscription?.id,
    subscription?.status,
    productionPaymentSessionStarted,
    paymentPending,
    paymentApprovedPendingActivation,
    membershipActive,
    existingOpen,
  ]);

  return (
    <main className="msc-page">
      <style>{styles}</style>
      <div className="msc-shell">
        <div className="msc-head">
          <button type="button" onClick={() => navigate(-1)}><ArrowLeft size={17} /> Volver</button>
          <button type="button" className="msc-refresh-button" onClick={load} disabled={loading}><RefreshCw size={16} /> Actualizar</button>
        </div>

        {!plan ? (
          <section className="msc-hero">
            <div className="msc-brand"><Crown size={24} /></div>
            <div className="msc-hero-copy">
              <span>Membresías Loop</span>
              <h1>Elige una membresía</h1>
              <p>{business?.name || "Negocio"}</p>
            </div>
          </section>
        ) : null}

        {needsAuth && !loading ? (
          <section className="msc-auth-card">
            <span className="msc-auth-icon"><LogIn size={20} /></span>
            <div>
              <strong>Inicia sesión para continuar</strong>
              <p>La suscripción debe quedar vinculada a tu cuenta de Loop. Después de iniciar sesión volverás automáticamente a este plan.</p>
            </div>
            <button type="button" onClick={goToLogin}>Iniciar sesión</button>
          </section>
        ) : null}

        {error ? <div className="msc-error">{error}</div> : null}
        {loading ? <div className="msc-state"><Loader2 className="msc-spin" size={22} /> Cargando...</div> : null}

        {!loading && !planId ? (
          <section className="msc-card">
            <div className="msc-section-title">
              <div><span>Planes disponibles</span><h2>Selecciona el plan que prefieras</h2></div>
              <em>{plans.length} opción{plans.length === 1 ? "" : "es"}</em>
            </div>
            {plans.length ? (
              <div className="msc-plan-list">
                {plans.map((item) => <PlanCard key={item.planId} plan={item} onSelect={selectPlan} />)}
              </div>
            ) : (
              <div className="msc-state">Este negocio todavía no tiene planes disponibles para venta online.</div>
            )}
          </section>
        ) : null}

        {!loading && !needsAuth && plan ? (
          <div className="msc-grid">
            <section className={`msc-card msc-summary-card msc-mobile-pane ${mobileStep === 1 ? "is-mobile-active" : ""}`}>
              <MobileCheckoutProgress
                step={1}
                label="Tu membresía"
                title="Esto es lo que estás activando"
                copy="Revisa lo esencial. Los detalles completos siguen disponibles sin llenar la pantalla."
              />

              <div className="msc-plan-title msc-plan-title-premium">
                <span className="msc-plan-icon"><Crown size={23} /></span>
                <div className="msc-plan-title-copy">
                  <span>Activa tu membresía</span>
                  <h1>{plan.name}</h1>
                  <p className={`msc-plan-description ${mobileDescriptionOpen ? "is-expanded" : ""}`}>
                    {plan.description || "Membresía recurrente del negocio."}
                  </p>
                  <button
                    type="button"
                    className="msc-mobile-only msc-description-toggle"
                    onClick={() => setMobileDescriptionOpen((value) => !value)}
                  >
                    {mobileDescriptionOpen ? "Ocultar detalles" : "Ver detalles"}
                  </button>
                </div>
                <div className="msc-summary-trust">
                  <ShieldCheck size={18} />
                  <div>
                    <strong>Checkout seguro</strong>
                    <span>Pago procesado con Wompi</span>
                  </div>
                </div>
              </div>

              <div className="msc-summary-grid">
                <div className="msc-summary-price"><CreditCard size={17} /><span>Precio</span><strong>{money(plan.price, plan.currency)} / {cycleLabel(plan.billingCycle)}</strong></div>
                <div className="msc-summary-benefit"><Ticket size={17} /><span>Beneficio</span><strong>{benefitLabel(plan)}</strong></div>
                <div className="msc-summary-cycle"><CalendarDays size={17} /><span>Renovación</span><strong>Mensual</strong></div>
                <div className="msc-summary-business"><Store size={17} /><span>Negocio</span><strong>{business?.name || "Negocio"}</strong></div>
              </div>

              <div className="msc-notice">
                {membershipActive ? <CheckCircle2 size={19} /> : paymentApprovedPendingActivation ? <CheckCircle2 size={19} /> : paymentPending ? <Loader2 className="msc-spin" size={19} /> : <ShieldCheck size={19} />}
                <div>
                  <strong>
                    {membershipActive
                      ? "Membresía activa"
                      : paymentApprovedPendingActivation
                        ? "Pago aprobado"
                        : paymentPending
                          ? "Procesando pago"
                          : "Pago seguro y renovación automática"}
                  </strong>
                  <span>
                    {membershipActive
                      ? "Tu pago fue aprobado y tus beneficios ya están activos."
                      : paymentApprovedPendingActivation
                        ? "El pago fue aprobado. Estamos terminando de activar tus beneficios."
                        : paymentPending
                          ? "Wompi está procesando tu pago de forma segura. Tus beneficios se activarán cuando el pago sea aprobado."
                          : productionPaymentAvailable ? `Pago mensual de ${money(plan.price, plan.currency)}. Puedes cancelar la renovación cuando quieras.` : "La compra online está temporalmente deshabilitada."}
                  </span>
                </div>
              </div>

              <div className="msc-mobile-only msc-mobile-wizard-actions">
                <button
                  type="button"
                  className="msc-mobile-primary"
                  onClick={() => goToMobileStep(2)}
                >
                  Continuar
                  <ChevronRight size={17} />
                </button>
              </div>
            </section>

            <section className="msc-card msc-checkout-card">
              <div className="msc-section-title msc-checkout-title msc-desktop-only">
                <div>
                  <span>{membershipActive ? "Activación completada" : "Finaliza tu activación"}</span>
                  <h2>
                    {membershipActive
                      ? "Tu membresía está lista"
                      : existingOpen
                        ? "Completa tu pago"
                        : "Revisa y confirma tu membresía"}
                  </h2>
                  <p>
                    {membershipActive
                      ? "El pago fue confirmado y tus beneficios ya están disponibles."
                      : existingOpen
                        ? "Tus condiciones ya están guardadas. Completa el pago seguro para activar tus beneficios."
                        : "Confirma las condiciones de tu plan antes de preparar el pago."}
                  </p>
                </div>
                <div className={`msc-step-pill ${membershipActive ? "msc-step-pill-active" : ""}`}>
                  {membershipActive ? "Activa" : existingOpen ? "Paso 2 de 2" : "Paso 1 de 2"}
                </div>
              </div>

              <div className={`msc-mobile-pane msc-terms-pane ${mobileStep === 2 ? "is-mobile-active" : ""}`}>
                <MobileCheckoutProgress
                  step={2}
                  label="Condiciones"
                  title="Confirma antes de continuar"
                  copy="Todo lo importante, en corto. Puedes abrir cada punto si quieres leer más."
                />

                {!membershipActive ? (
                  <div className="msc-subsection-head msc-desktop-only">
                    <div>
                      <span>Condiciones de la membresía</span>
                      <strong>{existingOpen ? "Aceptadas y guardadas" : "Revisa cada punto"}</strong>
                    </div>
                    {existingOpen ? <CheckCircle2 size={20} /> : <ShieldCheck size={20} />}
                  </div>
                ) : null}

                {!membershipActive && !existingOpen ? (
                  <button
                    type="button"
                    className={`msc-mobile-only msc-mobile-accept-all ${allAccepted ? "is-active" : ""}`}
                    onClick={() => setAllConsents(!allAccepted)}
                  >
                    <span className="msc-mobile-accept-all-icon">
                      {allAccepted ? <CheckCircle2 size={18} /> : <ShieldCheck size={18} />}
                    </span>
                    <span>
                      <strong>{allAccepted ? "Todo aceptado" : "Aceptar todas"}</strong>
                      <small>Conservamos cada consentimiento por separado.</small>
                    </span>
                  </button>
                ) : null}

                {!membershipActive ? (
                  <div className={`msc-consents ${existingOpen ? "msc-consents-saved" : ""}`}>
                    {consentItems.map((item) => {
                      const expanded = expandedConsent === item.key;
                      return (
                        <div
                          key={item.key}
                          className={`msc-consent-row ${expanded ? "is-expanded" : ""} ${existingOpen ? "is-saved" : ""}`}
                        >
                          <label>
                            {existingOpen ? (
                              <span className="msc-consent-status" aria-label="Aceptado"><CheckCircle2 size={16} /></span>
                            ) : (
                              <input
                                type="checkbox"
                                checked={Boolean(consents[item.key])}
                                onChange={(event) =>
                                  setConsents((prev) => ({
                                    ...prev,
                                    [item.key]: event.target.checked,
                                  }))
                                }
                              />
                            )}
                            <span className="msc-consent-copy">
                              <strong>{item.title}</strong>
                              <span className="msc-consent-short">{item.short}</span>
                              <span className="msc-consent-desktop-detail">{item.detail}</span>
                            </span>
                          </label>

                          <button
                            type="button"
                            className="msc-mobile-only msc-consent-detail-toggle"
                            onClick={() =>
                              setExpandedConsent((current) =>
                                current === item.key ? "" : item.key,
                              )
                            }
                          >
                            {expanded ? "Ocultar" : "Ver detalle"}
                          </button>

                          {expanded ? (
                            <div className="msc-mobile-only msc-consent-mobile-detail">
                              {item.detail}
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                ) : null}

                {!membershipActive && !existingOpen ? (
                  <button
                    type="button"
                    className="msc-primary msc-desktop-only"
                    disabled={!allAccepted || !preview.checkoutEligible || preview?.purchaseTerms?.paymentAvailable !== true || saving}
                    onClick={createDraft}
                  >
                    {saving ? <Loader2 className="msc-spin" size={18} /> : <BadgeCheck size={18} />}
                    {saving ? "Preparando pago..." : `Continuar y pagar ${money(plan.price, plan.currency)}`}
                  </button>
                ) : null}

                <div className="msc-mobile-only msc-mobile-wizard-actions msc-mobile-wizard-actions-split">
                  <button
                    type="button"
                    className="msc-mobile-secondary"
                    onClick={() => goToMobileStep(1)}
                    disabled={paymentFlowLocked}
                  >
                    <ArrowLeft size={16} />
                    Atrás
                  </button>
                  <button
                    type="button"
                    className="msc-mobile-primary"
                    disabled={
                      (!existingOpen && (!allAccepted || !preview.checkoutEligible || preview?.purchaseTerms?.paymentAvailable !== true)) ||
                      saving
                    }
                    onClick={continueMobileToPayment}
                  >
                    {saving ? <Loader2 className="msc-spin" size={17} /> : null}
                    {saving ? "Preparando..." : existingOpen ? "Volver al pago" : "Continuar al pago"}
                    {!saving ? <ChevronRight size={17} /> : null}
                  </button>
                </div>
              </div>

              <div className={`msc-mobile-pane msc-payment-pane ${mobileStep === 3 ? "is-mobile-active" : ""}`}>
                <MobileCheckoutProgress
                  step={3}
                  label="Pago"
                  title={membershipActive ? "Membresía activa" : "Finaliza tu membresía"}
                  copy={membershipActive ? "Tu pago está confirmado." : "Revisa el total y completa el pago seguro."}
                />

                <div className="msc-mobile-only msc-mobile-payment-summary">
                  <div>
                    <span>{plan.name}</span>
                    <strong>{money(plan.price, plan.currency)} / {cycleLabel(plan.billingCycle)}</strong>
                  </div>
                  <span className="msc-mobile-payment-ok"><CheckCircle2 size={15} /> Condiciones aceptadas</span>
                </div>

                {!paymentFlowLocked && !membershipActive ? (
                  <button
                    type="button"
                    className="msc-mobile-only msc-mobile-back-link"
                    onClick={() => goToMobileStep(2)}
                  >
                    <ArrowLeft size={15} />
                    Revisar condiciones
                  </button>
                ) : null}

                {!membershipActive && existingOpen ? (
                  <div className="msc-success msc-desktop-only">
                    <CheckCircle2 size={22} />
                    <div>
                      <strong>
                        {paymentApprovedPendingActivation
                          ? "Pago aprobado"
                          : paymentPending
                            ? "Confirmando tu pago"
                            : "Todo listo para pagar"}
                      </strong>
                      <span>
                        {paymentApprovedPendingActivation
                          ? "Wompi confirmó el pago. Estamos terminando de activar tus beneficios."
                          : paymentPending
                            ? "Estamos esperando la confirmación final de Wompi. Si tu banco solicita verificación, complétala en esta pantalla."
                            : `Tus condiciones están guardadas. Completa el pago de ${money(plan.price, plan.currency)} para activar la membresía.`}
                      </span>
                    </div>
                  </div>
                ) : null}

                {existingOpen && membershipActive ? (
                  <WompiProductionPaymentCard
                    subscription={subscription}
                    onSubscription={setSubscription}
                    onViewMembership={() => navigate("/my-memberships")}
                  />
                ) : null}

                {existingOpen && !membershipActive && !productionPaymentRouteReady && !productionAvailabilityError && ["draft", "pending_payment", "pending_activation"].includes(subscription.status) ? (
                  <div className="msc-production-state">
                    <Loader2 className="msc-spin" size={16} />
                    Verificando el medio de pago habilitado para esta membresía...
                  </div>
                ) : null}

                {existingOpen && !membershipActive && !productionPaymentSessionStarted && productionAvailabilityError && ["draft", "pending_payment", "pending_activation"].includes(subscription.status) ? (
                  <div className="msc-production-error">
                    <AlertTriangle size={16} />
                    <span>{productionAvailabilityError}</span>
                    <button
                      type="button"
                      onClick={() => setProductionAvailabilityAttempt((value) => value + 1)}
                    >
                      Reintentar
                    </button>
                  </div>
                ) : null}

                {existingOpen && !membershipActive && !productionPaymentSessionStarted && productionAvailabilityResolved && !productionPaymentAvailable && preview?.purchaseTerms?.sandboxPaymentAvailable === true && ["draft", "pending_payment", "pending_activation"].includes(subscription.status) ? (
                  <SandboxPaymentCard
                    subscription={subscription}
                    setup={sandboxSetup}
                    setupLoading={sandboxSetupLoading}
                    setupError={sandboxSetupError}
                    onLoadSetup={loadSandboxSetup}
                    onSubscription={setSubscription}
                  />
                ) : null}

                {existingOpen && !membershipActive && !productionPaymentSessionStarted && productionAvailabilityResolved && !productionPaymentAvailable && preview?.purchaseTerms?.sandboxPaymentAvailable !== true && ["draft", "pending_payment", "pending_activation"].includes(subscription.status) ? (
                  <div className="msc-production-error">
                    <AlertTriangle size={16} />
                    <span>La compra online está temporalmente deshabilitada. No se realizará ningún cobro.</span>
                  </div>
                ) : null}

                {existingOpen && !membershipActive && productionPaymentRouteReady && ["draft", "pending_payment", "pending_activation"].includes(subscription.status) ? (
                  <WompiProductionPaymentCard
                    subscription={subscription}
                    onSubscription={setSubscription}
                    onViewMembership={() => navigate("/my-memberships")}
                  />
                ) : null}
              </div>

              <div className="msc-actions">
                <button type="button" onClick={() => navigate("/my-memberships")}>Ver mis membresías <ChevronRight size={16} /></button>
                <a href="/politica-de-pagos">Revisar política de pagos</a>
              </div>
            </section>
          </div>
        ) : null}
      </div>
    </main>
  );
}

const styles = `
  .msc-production-card{margin-top:14px;border:1px solid #99f6e4;background:linear-gradient(180deg,#f0fdfa,#fff);border-radius:20px;padding:13px;display:grid;gap:10px}.msc-production-title{display:grid;grid-template-columns:auto 1fr;gap:9px;align-items:start}.msc-production-icon{width:34px;height:34px;border-radius:12px;background:#0f766e;color:#fff;display:grid;place-items:center}.msc-production-title span,.msc-production-title strong,.msc-production-title small{display:block}.msc-production-title>div>span{font-size:9px;letter-spacing:.12em;text-transform:uppercase;color:#0f766e;font-weight:950}.msc-production-title strong{font-size:12px;margin-top:2px}.msc-production-title small{font-size:10px;color:#64748b;line-height:1.4;margin-top:3px;font-weight:700}.msc-production-state,.msc-production-error,.msc-production-pending,.msc-production-approved,.msc-production-foot{border:1px solid #e2e8f0;background:#fff;border-radius:14px;padding:10px;font-size:10.5px;font-weight:800;display:flex;gap:8px;align-items:center}.msc-production-error{border-color:#fecaca;background:#fff1f2;color:#be123c}.msc-production-error button{margin-left:auto;border:1px solid #fda4af;background:#fff;color:#be123c;border-radius:999px;padding:6px 9px;font-size:9px;font-weight:950;cursor:pointer}.msc-production-fields{display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px}.msc-production-fields label{display:grid;gap:5px}.msc-production-fields span{font-size:9.5px;font-weight:900;color:#475569}.msc-production-fields input{width:100%;box-sizing:border-box;border:1px solid #cbd5e1;border-radius:12px;min-height:40px;padding:0 10px;font-size:12px;outline:none}.msc-production-fields input:focus{border-color:#14b8a6;box-shadow:0 0 0 3px rgba(20,184,166,.12)}.msc-production-wide{grid-column:1/-1}.msc-production-consent{border:1px solid #99f6e4;background:#fff;border-radius:15px;padding:10px;display:grid;grid-template-columns:auto 1fr;gap:8px;align-items:start;cursor:pointer}.msc-production-consent input{margin-top:3px;accent-color:#0f766e}.msc-production-consent span,.msc-production-consent strong{display:block}.msc-production-consent span{font-size:10.5px;line-height:1.4;color:#64748b;font-weight:700}.msc-production-consent strong{font-size:11px;color:#115e59;margin-bottom:2px}.msc-production-consent a{margin-top:5px;color:#0f766e;text-decoration:none;font-weight:950;display:inline-flex;align-items:center;gap:4px}.msc-production-primary{border:0;background:#0f766e;color:#fff;border-radius:14px;min-height:46px;padding:9px 12px;display:flex;align-items:center;justify-content:center;gap:8px;font-size:10.5px;font-weight:950;cursor:pointer}.msc-production-primary:disabled{opacity:.5;cursor:not-allowed}.msc-production-pending{border-color:#fde68a;background:#fffbeb;color:#92400e;align-items:flex-start}.msc-production-pending div,.msc-production-approved div{flex:1}.msc-production-pending strong,.msc-production-pending span,.msc-production-approved strong,.msc-production-approved span{display:block}.msc-production-pending span,.msc-production-approved span{font-size:9.8px;margin-top:2px;line-height:1.35}.msc-production-pending button,.msc-production-approved button{border:1px solid currentColor;background:#fff;color:inherit;border-radius:999px;padding:6px 9px;font-size:9px;font-weight:950;cursor:pointer}.msc-production-approved{border-color:#86efac;background:#f0fdf4;color:#166534;align-items:flex-start}.msc-production-three-ds{border:1px solid #cbd5e1;background:#fff;border-radius:15px;padding:10px}.msc-production-three-ds-head{display:flex;gap:9px;align-items:center;margin-bottom:8px}.msc-production-three-ds-head strong,.msc-production-three-ds-head span{display:block}.msc-production-three-ds-head strong{font-size:11px}.msc-production-three-ds-head span{font-size:9.5px;color:#64748b;margin-top:2px}.msc-production-foot{color:#64748b;background:#f8fafc;line-height:1.35}  .msc-ledger-inspector{margin-top:12px;border:1px dashed #b8c7d9;border-radius:16px;padding:14px;background:#f8fafc;display:grid;gap:8px}.msc-ledger-inspector>strong{font-size:12px;color:#0f766e}.msc-ledger-inspector>div{display:grid;grid-template-columns:150px minmax(0,1fr);gap:10px;align-items:start}.msc-ledger-inspector span{font-size:11px;font-weight:800;color:#64748b}.msc-ledger-inspector code{font-size:11px;white-space:normal;overflow-wrap:anywhere;color:#0f172a;background:#fff;border:1px solid #e2e8f0;border-radius:8px;padding:5px 7px}
  .msc-production-recovery{border:1px solid #bae6fd;background:#f0f9ff;color:#075985;border-radius:14px;padding:10px;font-size:10.5px;font-weight:850;display:flex;gap:8px;align-items:flex-start;line-height:1.4}
  .msc-production-diagnostic{border:1px dashed #cbd5e1;background:#f8fafc;border-radius:14px;padding:10px;display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px}
  .msc-production-diagnostic>div{min-width:0}
  .msc-production-diagnostic span,.msc-production-diagnostic strong{display:block}
  .msc-production-diagnostic span{font-size:9px;color:#64748b;font-weight:850;text-transform:uppercase;letter-spacing:.05em}
  .msc-production-diagnostic strong{margin-top:2px;font-size:10.5px;color:#0f172a;overflow-wrap:anywhere}
  .msc-page{min-height:100dvh;background:radial-gradient(circle at 10% 0%,rgba(0,103,95,.12),transparent 32%),linear-gradient(180deg,#f8fafc,#fff);padding:22px;color:#0f172a;box-sizing:border-box}.msc-shell{width:min(1040px,100%);margin:0 auto}.msc-head{display:flex;justify-content:space-between;gap:10px;margin-bottom:16px}.msc-head button,.msc-actions button,.msc-actions a{border:1px solid #dbe4ef;background:#fff;color:#334155;border-radius:999px;min-height:40px;padding:0 14px;display:inline-flex;align-items:center;justify-content:center;gap:7px;font-size:12px;font-weight:950;text-decoration:none;cursor:pointer}.msc-hero{display:flex;align-items:center;gap:14px;border:1px solid rgba(207,225,222,.95);background:linear-gradient(135deg,#fff,#f0fdfa);border-radius:30px;padding:22px;margin-bottom:16px;box-shadow:0 20px 48px rgba(15,23,42,.06)}.msc-brand,.msc-plan-icon{width:48px;height:48px;border-radius:18px;background:#00675f;color:#fff;display:grid;place-items:center;flex:0 0 auto}.msc-hero span,.msc-section-title span,.msc-plan-title>div>span{display:block;color:#00675f;font-size:10.5px;font-weight:950;letter-spacing:.13em;text-transform:uppercase}.msc-hero h1{margin:4px 0 0;font-size:34px;letter-spacing:-.055em}.msc-hero p{margin:5px 0 0;color:#64748b;font-weight:800}.msc-grid{display:grid;grid-template-columns:1.1fr .9fr;gap:16px}.msc-card{border:1px solid #dfe9ee;background:#fff;border-radius:28px;padding:20px;box-shadow:0 18px 44px rgba(15,23,42,.06)}.msc-section-title{display:flex;align-items:flex-start;justify-content:space-between;gap:12px;margin-bottom:16px}.msc-section-title h2,.msc-plan-title h2{margin:4px 0 0;font-size:21px;letter-spacing:-.035em}.msc-section-title em{font-style:normal;color:#00675f;background:#f0fdfa;border-radius:999px;padding:6px 9px;font-size:11px;font-weight:950}.msc-plan-list{display:grid;gap:10px}.msc-plan-card{width:100%;border:1px solid #dfe9ee;background:#fbfefd;border-radius:22px;padding:14px;display:grid;grid-template-columns:auto minmax(0,1fr) auto auto;gap:12px;align-items:center;text-align:left;cursor:pointer;color:inherit}.msc-plan-card:hover{border-color:#9fd3cd;transform:translateY(-1px)}.msc-plan-card .msc-plan-icon{width:42px;height:42px;border-radius:15px}.msc-plan-copy strong,.msc-plan-copy small,.msc-plan-copy em,.msc-plan-price strong,.msc-plan-price small{display:block}.msc-plan-copy strong{font-size:15px;font-weight:950}.msc-plan-copy small{margin-top:3px;color:#64748b;font-size:12px;font-weight:700}.msc-plan-copy em{margin-top:7px;color:#00675f;font-style:normal;font-size:11px;font-weight:900;display:flex;gap:5px;align-items:center}.msc-plan-price{text-align:right}.msc-plan-price strong{font-size:16px}.msc-plan-price small{color:#64748b;font-size:10.5px}.msc-plan-title{display:flex;gap:12px;align-items:flex-start}.msc-plan-title p{margin:7px 0 0;color:#64748b;line-height:1.45;font-size:13px;font-weight:700}.msc-summary-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-top:18px}.msc-summary-grid>div{border:1px solid #e2e8f0;background:#f8fafc;border-radius:18px;padding:12px;display:grid;grid-template-columns:auto 1fr;column-gap:8px}.msc-summary-grid svg{grid-row:1/3;color:#00675f}.msc-summary-grid span{font-size:10px;color:#64748b;font-weight:850}.msc-summary-grid strong{font-size:12px;font-weight:950}.msc-notice,.msc-success{margin-top:14px;border:1px solid rgba(0,103,95,.18);background:#f0fdfa;color:#00675f;border-radius:18px;padding:13px;display:flex;gap:10px;align-items:flex-start}.msc-notice strong,.msc-notice span,.msc-success strong,.msc-success span{display:block}.msc-notice span,.msc-success span{margin-top:3px;font-size:11.5px;line-height:1.4;font-weight:750}.msc-consents{display:grid;gap:9px}.msc-consents label{border:1px solid #e2e8f0;border-radius:17px;padding:11px;display:grid;grid-template-columns:auto 1fr;gap:9px;align-items:flex-start;cursor:pointer}.msc-consents input{margin-top:3px;accent-color:#00675f}.msc-consents input:disabled{opacity:.65}.msc-consents strong,.msc-consents span{display:block}.msc-consents strong{font-size:12.5px}.msc-consents span{color:#64748b;font-size:11.5px;line-height:1.4;font-weight:700}.msc-primary{width:100%;margin-top:14px;min-height:46px;border:0;border-radius:16px;background:#00675f;color:#fff;font-weight:950;display:flex;align-items:center;justify-content:center;gap:8px;cursor:pointer}.msc-primary:disabled{opacity:.5;cursor:not-allowed}.msc-actions{display:flex;justify-content:space-between;gap:8px;flex-wrap:wrap;margin-top:12px}.msc-state,.msc-error{border:1px solid #e2e8f0;background:#fff;border-radius:22px;padding:18px;color:#64748b;font-weight:850;display:flex;align-items:center;gap:9px}.msc-error{border-color:#fed7aa;background:#fff7ed;color:#c2410c;margin-bottom:14px}.msc-auth-card{border:1px solid rgba(0,103,95,.18);background:linear-gradient(135deg,#fff,#f0fdfa);border-radius:22px;padding:16px;margin-bottom:14px;display:grid;grid-template-columns:auto minmax(0,1fr) auto;gap:12px;align-items:center}.msc-auth-icon{width:42px;height:42px;border-radius:15px;background:#00675f;color:#fff;display:grid;place-items:center}.msc-auth-card strong{display:block;font-size:14px}.msc-auth-card p{margin:4px 0 0;color:#64748b;font-size:11.5px;line-height:1.45;font-weight:700}.msc-auth-card button{min-height:40px;border:0;border-radius:999px;background:#00675f;color:#fff;padding:0 15px;font-size:11px;font-weight:950;cursor:pointer}.msc-sandbox-card{margin-top:14px;border:1px solid #c4b5fd;background:linear-gradient(180deg,#faf5ff,#fff);border-radius:20px;padding:13px;display:grid;gap:10px}.msc-sandbox-title{display:grid;grid-template-columns:auto 1fr;gap:9px;align-items:start}.msc-sandbox-icon{width:34px;height:34px;border-radius:12px;background:#6d28d9;color:#fff;display:grid;place-items:center}.msc-sandbox-title span,.msc-sandbox-title strong,.msc-sandbox-title small{display:block}.msc-sandbox-title>div>span{font-size:9px;letter-spacing:.12em;text-transform:uppercase;color:#6d28d9;font-weight:950}.msc-sandbox-title strong{font-size:12px;margin-top:2px}.msc-sandbox-title small{font-size:10px;color:#64748b;line-height:1.4;margin-top:3px;font-weight:700}.msc-sandbox-state,.msc-sandbox-error,.msc-payment-pending,.msc-payment-approved,.msc-sandbox-foot{border:1px solid #e2e8f0;background:#fff;border-radius:14px;padding:10px;font-size:10.5px;font-weight:800;display:flex;gap:8px;align-items:center}.msc-sandbox-error{border-color:#fed7aa;background:#fff7ed;color:#c2410c}.msc-sandbox-error button{margin-left:auto;border:1px solid #fdba74;background:#fff;color:#c2410c;border-radius:999px;padding:6px 9px;font-size:9px;font-weight:950;cursor:pointer}.msc-wompi-consent{border:1px solid #ddd6fe;background:#fff;border-radius:15px;padding:10px;display:grid;grid-template-columns:auto 1fr;gap:8px;align-items:start;cursor:pointer}.msc-wompi-consent input{margin-top:3px;accent-color:#6d28d9}.msc-wompi-consent span,.msc-wompi-consent strong{display:block}.msc-wompi-consent span{font-size:10.5px;line-height:1.4;color:#64748b;font-weight:700}.msc-wompi-consent strong{font-size:11px;color:#312e81;margin-bottom:2px}.msc-wompi-consent a{margin-top:5px;color:#6d28d9;text-decoration:none;font-weight:950;display:inline-flex;align-items:center;gap:4px}.msc-sandbox-scenarios{display:grid;gap:7px}.msc-sandbox-scenarios button{border:1px solid #ddd6fe;background:#fff;color:#312e81;border-radius:14px;min-height:46px;padding:9px 10px;display:flex;align-items:center;gap:8px;text-align:left;cursor:pointer}.msc-sandbox-scenarios button:disabled{opacity:.5;cursor:not-allowed}.msc-sandbox-scenarios button span,.msc-sandbox-scenarios button strong,.msc-sandbox-scenarios button small{display:block}.msc-sandbox-scenarios button strong{font-size:10.5px}.msc-sandbox-scenarios button small{font-size:9.5px;color:#64748b;margin-top:2px}.msc-payment-pending{border-color:#fde68a;background:#fffbeb;color:#92400e;align-items:flex-start}.msc-payment-pending div{flex:1}.msc-payment-pending strong,.msc-payment-pending span{display:block}.msc-payment-pending span{font-size:9.8px;margin-top:2px;line-height:1.35}.msc-payment-pending button{border:1px solid #fcd34d;background:#fff;color:#92400e;border-radius:999px;padding:6px 9px;font-size:9px;font-weight:950;cursor:pointer}.msc-payment-approved{border-color:#86efac;background:#f0fdf4;color:#166534;align-items:flex-start}.msc-payment-approved div{flex:1}.msc-payment-approved strong,.msc-payment-approved span{display:block}.msc-payment-approved span{font-size:9.8px;margin-top:2px;line-height:1.35}.msc-payment-approved button{border:1px solid #86efac;background:#fff;color:#166534;border-radius:999px;padding:6px 9px;font-size:9px;font-weight:950;cursor:pointer}.msc-three-ds{border:1px solid #cbd5e1;background:#fff;border-radius:15px;padding:10px}.msc-three-ds-head{display:flex;gap:9px;align-items:center;margin-bottom:8px}.msc-three-ds-head img{width:74px;max-height:34px;object-fit:contain}.msc-three-ds-head strong,.msc-three-ds-head span{display:block}.msc-three-ds-head strong{font-size:11px}.msc-three-ds-head span{font-size:9.5px;color:#64748b;margin-top:2px}.msc-3ds-hidden{width:1px;height:1px;border:0;position:absolute;left:-9999px;top:-9999px}.msc-3ds-challenge{width:100%;height:480px;border:1px solid #e2e8f0;border-radius:12px;background:#fff}.msc-sandbox-foot{color:#64748b;background:#f8fafc;line-height:1.35}.msc-spin{animation:msc-spin 1s linear infinite}@keyframes msc-spin{to{transform:rotate(360deg)}}@media(max-width:780px){.msc-auth-card{grid-template-columns:auto 1fr}.msc-auth-card button{grid-column:1/-1;width:100%}.msc-page{padding:14px 12px calc(var(--loop-mobile-nav-height,72px) + 20px)}.msc-grid{grid-template-columns:1fr}.msc-hero{border-radius:24px;padding:18px}.msc-hero h1{font-size:28px}.msc-card{border-radius:23px;padding:16px}.msc-plan-card{grid-template-columns:auto minmax(0,1fr) auto}.msc-plan-price{grid-column:2/3;text-align:left}.msc-plan-card>svg{grid-column:3;grid-row:1/3}.msc-summary-grid{grid-template-columns:1fr}.msc-actions{display:grid}.msc-actions>*{width:100%;box-sizing:border-box}.msc-payment-pending{display:grid;grid-template-columns:auto 1fr}.msc-payment-pending button{grid-column:1/-1;width:100%}.msc-3ds-challenge{height:500px}}

  /* LOOP_CHECKOUT_PREMIUM_UX_V1 */
  .msc-page{
    background:
      radial-gradient(circle at 8% 0%,rgba(0,103,95,.09),transparent 30%),
      linear-gradient(180deg,#f7faf9 0%,#fbfcfc 42%,#fff 100%);
    padding:30px 22px 44px;
  }
  .msc-shell{width:min(1180px,100%)}
  .msc-head{margin-bottom:14px}
  .msc-head button{
    min-height:42px;
    padding:0 16px;
    font-size:12px;
    box-shadow:0 5px 18px rgba(15,23,42,.04);
  }
  .msc-hero{
    position:relative;
    display:grid;
    grid-template-columns:auto minmax(0,1fr) auto;
    border-radius:26px;
    padding:22px 24px;
    margin-bottom:18px;
    border:1px solid #d9e6e3;
    background:#fff;
    box-shadow:0 18px 44px rgba(15,23,42,.055);
  }
  .msc-hero-copy{min-width:0}
  .msc-hero h1{font-size:32px;line-height:1.04;margin-top:5px}
  .msc-hero p{font-size:13px;margin-top:7px}
  .msc-hero-trust{
    align-self:center;
    display:grid;
    grid-template-columns:auto 1fr;
    gap:9px;
    align-items:center;
    border:1px solid #d8eee9;
    background:#f3fbf8;
    color:#00675f;
    border-radius:16px;
    padding:10px 13px;
    min-width:150px;
  }
  .msc-hero-trust strong,.msc-hero-trust span{display:block}
  .msc-hero-trust strong{font-size:11.5px}
  .msc-hero-trust span{margin-top:2px;font-size:9.5px;letter-spacing:0;text-transform:none;color:#5b706e;font-weight:800}

  .msc-grid{
    grid-template-columns:minmax(330px,.82fr) minmax(520px,1.18fr);
    gap:20px;
    align-items:start;
  }
  .msc-card{
    border-radius:24px;
    border-color:#dce7e5;
    padding:22px;
    box-shadow:0 14px 38px rgba(15,23,42,.05);
  }
  .msc-summary-card{
    position:sticky;
    top:92px;
    background:linear-gradient(180deg,#fff 0%,#fbfdfc 100%);
  }
  .msc-plan-title h2{font-size:23px;line-height:1.1}
  .msc-plan-title p{font-size:12.5px;line-height:1.55}
  .msc-summary-grid{gap:10px;margin-top:20px}
  .msc-summary-grid>div{
    border-radius:15px;
    padding:13px;
    background:#f8fbfa;
    border-color:#e2ebe9;
  }
  .msc-summary-grid span{font-size:9.5px}
  .msc-summary-grid strong{font-size:12.5px}
  .msc-notice{
    margin-top:16px;
    border-radius:16px;
    padding:14px;
    background:#effaf7;
  }
  .msc-notice strong{font-size:12.5px}
  .msc-notice span{font-size:11px;line-height:1.48}

  .msc-checkout-card{padding:26px}
  .msc-checkout-title{margin-bottom:20px}
  .msc-checkout-title h2{font-size:25px;line-height:1.12;margin-top:6px}
  .msc-checkout-title p{
    margin:7px 0 0;
    color:#64748b;
    font-size:12px;
    line-height:1.55;
    font-weight:700;
    max-width:560px;
  }
  .msc-step-pill{
    flex:0 0 auto;
    border:1px solid #d7e8e4;
    background:#f4faf8;
    color:#00675f;
    border-radius:999px;
    padding:7px 10px;
    font-size:10px;
    font-weight:950;
    white-space:nowrap;
  }
  .msc-subsection-head{
    display:flex;
    align-items:center;
    justify-content:space-between;
    gap:12px;
    margin:2px 0 10px;
    padding:0 2px;
    color:#00675f;
  }
  .msc-subsection-head span,.msc-subsection-head strong{display:block}
  .msc-subsection-head span{
    color:#64748b;
    font-size:9.5px;
    font-weight:900;
    text-transform:uppercase;
    letter-spacing:.09em;
  }
  .msc-subsection-head strong{font-size:13px;margin-top:2px}

  .msc-consents{gap:7px}
  .msc-consents label{
    border-radius:14px;
    padding:11px 12px;
    gap:10px;
    border-color:#e2e8e7;
    background:#fff;
    transition:border-color .15s ease,background .15s ease,box-shadow .15s ease;
  }
  .msc-consents label:hover{border-color:#b7d8d1;background:#fcfefd}
  .msc-consents input{
    width:16px;height:16px;
    margin-top:2px;
    flex:0 0 auto;
  }
  .msc-consents strong{font-size:12px;color:#243434}
  .msc-consents span{font-size:10.7px;line-height:1.45;color:#6b7b79}
  .msc-consents-saved label{background:#f8fbfa;border-color:#e3eeeb}
  .msc-consents-saved input:disabled{opacity:1;filter:none}
  .msc-success{
    border-radius:16px;
    padding:14px 15px;
    margin-top:14px;
    background:#eefaf6;
  }
  .msc-success strong{font-size:13px}
  .msc-success span{font-size:11px;line-height:1.5}
  .msc-primary{
    min-height:50px;
    border-radius:14px;
    font-size:12.5px;
    box-shadow:0 10px 24px rgba(0,103,95,.18);
  }
  .msc-actions{
    border-top:1px solid #edf1f0;
    margin-top:18px;
    padding-top:14px;
  }
  .msc-actions button,.msc-actions a{
    min-height:36px;
    border:0;
    background:transparent;
    padding:0 4px;
    color:#526664;
    font-size:10.5px;
  }

  /* Premium Wompi surface */
  .msc-production-card{
    margin-top:18px;
    border:1px solid #d6e8e4;
    background:#fff;
    border-radius:20px;
    padding:20px;
    gap:16px;
    box-shadow:0 14px 34px rgba(15,23,42,.045);
  }
  .msc-production-title{
    grid-template-columns:auto minmax(0,1fr);
    gap:12px;
    padding-bottom:15px;
    border-bottom:1px solid #edf2f1;
  }
  .msc-production-icon{
    width:42px;height:42px;border-radius:13px;
    background:#00675f;
  }
  .msc-production-title>div>span{font-size:9.5px}
  .msc-production-title strong{font-size:17px;line-height:1.2;margin-top:3px;color:#172725}
  .msc-production-title small{font-size:10.5px;line-height:1.5;margin-top:5px;max-width:620px}
  .msc-production-form-head{
    display:flex;
    justify-content:space-between;
    align-items:flex-end;
    gap:12px;
  }
  .msc-production-form-head span,.msc-production-form-head strong{display:block}
  .msc-production-form-head span{
    font-size:9px;font-weight:950;text-transform:uppercase;letter-spacing:.1em;color:#71817f
  }
  .msc-production-form-head strong{font-size:14px;margin-top:2px;color:#1d2e2c}
  .msc-production-security{
    display:flex;align-items:center;gap:6px;color:#00675f;font-size:9.5px;font-weight:900
  }
  .msc-production-fields{
    grid-template-columns:1fr 1fr 1fr;
    gap:11px;
  }
  .msc-production-fields label{gap:6px}
  .msc-production-fields span{font-size:10px;color:#394b49}
  .msc-production-fields input{
    min-height:48px;
    border-radius:12px;
    border-color:#cfdad8;
    padding:0 13px;
    font-size:13px;
    background:#fff;
  }
  .msc-production-fields input::placeholder{color:#a1afad}
  .msc-production-fields input:focus{
    border-color:#00877c;
    box-shadow:0 0 0 3px rgba(0,135,124,.10);
  }
  .msc-production-consent{
    border-radius:14px;
    border-color:#dbe9e6;
    background:#f8fbfa;
    padding:13px;
    gap:10px;
  }
  .msc-production-consent input{width:16px;height:16px;margin-top:2px}
  .msc-production-consent strong{font-size:11.5px;color:#1f3a37}
  .msc-production-consent span{font-size:10.5px;line-height:1.5}
  .msc-production-primary{
    min-height:52px;
    border-radius:14px;
    background:#00675f;
    font-size:12.5px;
    box-shadow:0 10px 25px rgba(0,103,95,.20);
  }
  .msc-production-primary:not(:disabled):hover{background:#005b54;transform:translateY(-1px)}
  .msc-production-foot{
    border:0;
    border-radius:12px;
    background:#f7f9f9;
    padding:11px 12px;
    font-size:9.8px;
    color:#667876;
  }
  .msc-production-state,.msc-production-error,.msc-production-pending,.msc-production-approved{
    border-radius:14px;
    padding:12px;
    font-size:10.5px;
  }
  .msc-production-three-ds{border-radius:16px;padding:14px}
  .msc-production-three-ds-head strong{font-size:13px}
  .msc-production-three-ds-head span{font-size:10.5px}

  @media(max-width:900px){
    .msc-grid{grid-template-columns:1fr}
    .msc-summary-card{position:static}
  }
  @media(max-width:780px){
    .msc-page{padding:16px 12px calc(var(--loop-mobile-nav-height,72px) + 24px)}
    .msc-hero{grid-template-columns:auto 1fr;padding:18px;border-radius:22px}
    .msc-hero-trust{grid-column:1/-1;width:100%;box-sizing:border-box;margin-top:4px}
    .msc-hero h1{font-size:27px}
    .msc-card,.msc-checkout-card{padding:17px;border-radius:20px}
    .msc-checkout-title{display:grid}
    .msc-step-pill{justify-self:start}
    .msc-production-card{padding:15px;border-radius:18px}
    .msc-production-fields{grid-template-columns:1fr 1fr}
    .msc-production-wide{grid-column:1/-1}
    .msc-production-fields label:last-child{grid-column:auto}
    .msc-production-form-head{align-items:flex-start;flex-direction:column}
  }


  /* LOOP_CHECKOUT_PREMIUM_UX_V2 */
  .msc-grid{
    grid-template-columns:1fr;
    gap:18px;
    width:min(980px,100%);
    margin:0 auto;
  }
  .msc-summary-card{
    position:static;
    top:auto;
    padding:25px 26px;
  }
  .msc-plan-title-premium{
    display:grid;
    grid-template-columns:auto minmax(0,1fr) auto;
    gap:14px;
    align-items:center;
  }
  .msc-plan-title-copy h1{
    margin:4px 0 0;
    font-size:28px;
    line-height:1.06;
    letter-spacing:-.045em;
    color:#172725;
  }
  .msc-plan-title-copy p{
    margin-top:7px;
    max-width:600px;
  }
  .msc-summary-trust{
    display:grid;
    grid-template-columns:auto 1fr;
    align-items:center;
    gap:8px;
    color:#00675f;
    border:1px solid #d6e9e5;
    background:#f4faf8;
    border-radius:15px;
    padding:10px 12px;
  }
  .msc-summary-trust strong,
  .msc-summary-trust span{display:block}
  .msc-summary-trust strong{font-size:10.5px}
  .msc-summary-trust span{
    margin-top:2px;
    color:#687b78;
    font-size:9px;
    font-weight:800;
  }
  .msc-summary-grid{
    grid-template-columns:repeat(4,minmax(0,1fr));
    margin-top:19px;
  }
  .msc-notice{
    max-width:none;
  }
  .msc-checkout-card{
    width:100%;
    box-sizing:border-box;
  }
  .msc-consents-saved label{
    cursor:default;
  }
  .msc-consent-status{
    width:20px;
    height:20px;
    display:grid;
    place-items:center;
    color:#007c72;
    flex:0 0 auto;
    margin-top:0;
  }
  .msc-consents-saved label:hover{
    border-color:#e3eeeb;
    background:#f8fbfa;
  }

  @media(max-width:780px){
    .msc-plan-title-premium{
      grid-template-columns:auto minmax(0,1fr);
      align-items:start;
    }
    .msc-summary-trust{
      grid-column:1/-1;
      width:100%;
      box-sizing:border-box;
    }
    .msc-summary-grid{
      grid-template-columns:1fr 1fr;
    }
    .msc-plan-title-copy h1{font-size:24px}
  }
  @media(max-width:520px){
    .msc-summary-grid{grid-template-columns:1fr}
  }


  /* LOOP_PAYMENT_CARD_PREMIUM_UX_V2 */
  .msc-production-title-clean{
    grid-template-columns:minmax(0,1fr) auto;
    align-items:center;
    gap:18px;
  }
  .msc-production-kicker{
    display:flex!important;
    align-items:center;
    gap:5px;
  }
  .msc-payment-brands{
    display:flex;
    align-items:center;
    gap:8px;
    flex:0 0 auto;
  }
  .msc-payment-brand{
    height:30px;
    min-width:52px;
    box-sizing:border-box;
    border:1px solid #e1e7e6;
    background:#fff;
    border-radius:8px;
    display:flex;
    align-items:center;
    justify-content:center;
    opacity:.55;
    transition:opacity .15s ease,border-color .15s ease,box-shadow .15s ease;
  }
  .msc-payment-brand.is-active{
    opacity:1;
    border-color:#9acfc8;
    box-shadow:0 0 0 2px rgba(0,103,95,.07);
  }
  .msc-payment-brand-visa{
    font-size:12px;
    font-weight:1000;
    font-style:italic;
    letter-spacing:-.06em;
    color:#172554;
  }
  .msc-payment-brand-mastercard{
    position:relative;
    min-width:78px;
    padding-left:27px;
    padding-right:7px;
    justify-content:flex-start;
    overflow:hidden;
  }
  .msc-payment-brand-mastercard i{
    position:absolute;
    width:18px;
    height:18px;
    border-radius:50%;
    top:5px;
  }
  .msc-payment-brand-mastercard i:first-child{
    left:7px;
    background:#eb001b;
  }
  .msc-payment-brand-mastercard i:nth-child(2){
    left:17px;
    background:#f79e1b;
    opacity:.92;
  }
  .msc-payment-brand-mastercard b{
    position:relative;
    z-index:2;
    font-size:7px;
    letter-spacing:-.02em;
    color:#1f2937;
    font-weight:900;
  }
  .msc-production-fields input{
    font-variant-numeric:tabular-nums;
    letter-spacing:.015em;
  }

  @media(max-width:620px){
    .msc-production-title-clean{
      grid-template-columns:1fr;
    }
    .msc-payment-brands{
      justify-content:flex-start;
    }
  }


  /* LOOP_CHECKOUT_PREMIUM_UX_V3 */
  .msc-consents-saved{
    grid-template-columns:1fr 1fr;
  }
  .msc-consents-saved label{
    min-height:58px;
    box-sizing:border-box;
    padding:10px 11px;
  }
  .msc-step-pill-active{
    background:#e8f8f3;
    border-color:#b9e4d8;
    color:#00675f;
  }
  .msc-field-error{
    display:block;
    margin-top:-1px;
    color:#b42318;
    font-size:9.5px;
    line-height:1.25;
    font-weight:850;
  }
  .msc-production-fields input[aria-invalid="true"]{
    border-color:#e7a19a;
    box-shadow:0 0 0 3px rgba(180,35,24,.07);
  }
  .msc-payment-breakdown{
    display:grid;
    grid-template-columns:repeat(3,minmax(0,1fr));
    gap:8px;
    border:1px solid #e2ecea;
    background:#f8fbfa;
    border-radius:14px;
    padding:10px;
  }
  .msc-payment-breakdown>div{
    padding:2px 7px;
    border-right:1px solid #e2ecea;
  }
  .msc-payment-breakdown>div:last-child{border-right:0}
  .msc-payment-breakdown span,
  .msc-payment-breakdown strong{display:block}
  .msc-payment-breakdown span{
    color:#70817f;
    font-size:9px;
    font-weight:850;
  }
  .msc-payment-breakdown strong{
    margin-top:3px;
    color:#203431;
    font-size:11.5px;
    font-weight:950;
  }
  .msc-membership-complete{
    border:1px solid #c7e6dc;
    background:linear-gradient(180deg,#f4fbf8 0%,#fff 100%);
    border-radius:20px;
    padding:26px;
    text-align:center;
    display:grid;
    justify-items:center;
    gap:7px;
  }
  .msc-membership-complete-icon{
    width:58px;
    height:58px;
    border-radius:50%;
    display:grid;
    place-items:center;
    background:#00675f;
    color:#fff;
    box-shadow:0 10px 24px rgba(0,103,95,.18);
  }
  .msc-membership-complete-kicker{
    margin-top:5px;
    color:#00675f;
    font-size:9.5px;
    font-weight:950;
    letter-spacing:.1em;
    text-transform:uppercase;
  }
  .msc-membership-complete h3{
    margin:0;
    color:#172725;
    font-size:24px;
    letter-spacing:-.035em;
  }
  .msc-membership-complete p{
    margin:0;
    max-width:520px;
    color:#64748b;
    font-size:11.5px;
    line-height:1.5;
    font-weight:700;
  }
  .msc-membership-complete-grid{
    width:min(520px,100%);
    display:grid;
    grid-template-columns:1fr 1fr;
    gap:8px;
    margin-top:8px;
  }
  .msc-membership-complete-grid>div{
    border:1px solid #e2ecea;
    background:#fff;
    border-radius:13px;
    padding:11px;
  }
  .msc-membership-complete-grid span,
  .msc-membership-complete-grid strong{display:block}
  .msc-membership-complete-grid span{
    color:#748582;
    font-size:9px;
    font-weight:850;
  }
  .msc-membership-complete-grid strong{
    margin-top:3px;
    color:#1f3733;
    font-size:12px;
    font-weight:950;
  }
  .msc-membership-complete-cta{
    margin-top:8px;
    min-height:44px;
    border:0;
    border-radius:13px;
    background:#00675f;
    color:#fff;
    padding:0 18px;
    font-size:11.5px;
    font-weight:950;
    cursor:pointer;
    box-shadow:0 9px 20px rgba(0,103,95,.16);
  }
  @media(max-width:780px){
    .msc-consents-saved{grid-template-columns:1fr}
    .msc-consents-saved label{min-height:0;padding:9px 10px}
    .msc-payment-breakdown{grid-template-columns:1fr}
    .msc-payment-breakdown>div{
      border-right:0;
      border-bottom:1px solid #e2ecea;
      padding:5px 3px 8px;
    }
    .msc-payment-breakdown>div:last-child{
      border-bottom:0;
      padding-bottom:3px;
    }
    .msc-membership-complete{padding:20px 14px}
    .msc-membership-complete-grid{grid-template-columns:1fr}
    .msc-membership-complete-cta{width:100%}
  }

  /* LOOP_CHECKOUT_MOBILE_WIZARD_V1 */
  .msc-mobile-only,
  .msc-mobile-progress,
  .msc-consent-short,
  .msc-consent-mobile-detail,
  .msc-production-consent-mobile-text{
    display:none;
  }
  .msc-consent-desktop-detail,
  .msc-production-consent-desktop-text{
    display:block;
  }
  .msc-consent-row{
    min-width:0;
  }

  @media(max-width:780px){
    .msc-mobile-only{
      display:flex;
    }
    .msc-desktop-only{
      display:none!important;
    }
    .msc-mobile-pane{
      display:none!important;
    }
    .msc-mobile-pane.is-mobile-active{
      display:block!important;
    }

    .msc-page{
      padding:12px 12px calc(var(--loop-mobile-nav-height,72px) + 22px);
      background:#f7faf9;
    }
    .msc-shell{
      width:100%;
    }
    .msc-head{
      margin:0 0 10px;
    }
    .msc-head button{
      min-height:38px;
      padding:0 12px;
      font-size:11px;
      box-shadow:none;
    }
    .msc-head .msc-refresh-button{
      display:none;
    }

    .msc-grid{
      gap:0;
      width:100%;
    }
    .msc-card,
    .msc-checkout-card,
    .msc-summary-card{
      width:100%;
      box-sizing:border-box;
      border-radius:20px;
      padding:16px;
      border-color:#e2ebe8;
      box-shadow:0 10px 28px rgba(15,23,42,.045);
    }
    .msc-checkout-card{
      background:#fff;
    }

    .msc-mobile-progress{
      display:block;
      margin-bottom:18px;
    }
    .msc-mobile-progress-top{
      display:flex;
      align-items:center;
      justify-content:space-between;
      gap:12px;
      color:#00675f;
      font-size:10px;
      font-weight:950;
    }
    .msc-mobile-progress-top span{
      letter-spacing:.08em;
      text-transform:uppercase;
    }
    .msc-mobile-progress-top strong{
      color:#536966;
      font-size:10px;
    }
    .msc-mobile-progress-bars{
      display:grid;
      grid-template-columns:repeat(3,1fr);
      gap:5px;
      margin:9px 0 16px;
    }
    .msc-mobile-progress-bars i{
      height:3px;
      border-radius:999px;
      background:#e5ecea;
    }
    .msc-mobile-progress-bars i.is-active{
      background:#00675f;
    }
    .msc-mobile-progress h2{
      margin:0;
      color:#172725;
      font-size:23px;
      line-height:1.08;
      letter-spacing:-.035em;
    }
    .msc-mobile-progress p{
      margin:6px 0 0;
      color:#71817f;
      font-size:11px;
      line-height:1.45;
      font-weight:700;
    }

    /* Step 1 */
    .msc-plan-title-premium{
      grid-template-columns:auto minmax(0,1fr);
      gap:11px;
      align-items:start;
    }
    .msc-plan-title-premium .msc-plan-icon{
      width:42px;
      height:42px;
      border-radius:14px;
    }
    .msc-plan-title-copy>span{
      font-size:9px;
    }
    .msc-plan-title-copy h1{
      margin-top:3px;
      font-size:23px;
      line-height:1.08;
    }
    .msc-plan-description{
      display:-webkit-box;
      -webkit-line-clamp:2;
      -webkit-box-orient:vertical;
      overflow:hidden;
      margin:5px 0 0!important;
      font-size:11px!important;
      line-height:1.45!important;
    }
    .msc-plan-description.is-expanded{
      display:block;
      overflow:visible;
    }
    .msc-description-toggle{
      width:max-content;
      border:0;
      background:transparent;
      padding:5px 0 0;
      color:#00675f;
      font-size:10px;
      font-weight:950;
      cursor:pointer;
    }
    .msc-summary-trust{
      grid-column:1/-1;
      width:100%;
      box-sizing:border-box;
      grid-template-columns:auto 1fr;
      border-radius:12px;
      padding:8px 10px;
      margin-top:3px;
    }
    .msc-summary-trust strong{
      font-size:10px;
    }
    .msc-summary-trust span{
      font-size:8.8px;
    }
    .msc-summary-grid{
      grid-template-columns:1fr;
      gap:7px;
      margin-top:14px;
    }
    .msc-summary-grid>div{
      grid-template-columns:auto minmax(0,1fr) auto;
      align-items:center;
      gap:8px;
      padding:10px 11px;
      border-radius:13px;
      background:#f9fbfa;
    }
    .msc-summary-grid svg{
      grid-row:auto;
    }
    .msc-summary-grid span{
      font-size:9.5px;
    }
    .msc-summary-grid strong{
      justify-self:end;
      text-align:right;
      font-size:11px;
    }
    .msc-summary-business{
      display:none!important;
    }
    .msc-summary-card>.msc-notice{
      display:none;
    }

    /* Shared mobile actions */
    .msc-mobile-wizard-actions{
      width:100%;
      box-sizing:border-box;
      margin-top:16px;
      gap:8px;
    }
    .msc-mobile-wizard-actions-split{
      display:grid;
      grid-template-columns:auto minmax(0,1fr);
    }
    .msc-mobile-primary,
    .msc-mobile-secondary{
      min-height:50px;
      border-radius:14px;
      padding:0 15px;
      display:flex;
      align-items:center;
      justify-content:center;
      gap:7px;
      font-size:11.5px;
      font-weight:950;
      cursor:pointer;
      box-sizing:border-box;
    }
    .msc-mobile-primary{
      flex:1;
      border:0;
      background:#00675f;
      color:#fff;
      box-shadow:0 9px 22px rgba(0,103,95,.17);
    }
    .msc-mobile-secondary{
      border:1px solid #dce7e4;
      background:#fff;
      color:#4d625f;
    }
    .msc-mobile-primary:disabled,
    .msc-mobile-secondary:disabled{
      opacity:.45;
      cursor:not-allowed;
      box-shadow:none;
    }

    /* Step 2 */
    .msc-mobile-accept-all{
      width:100%;
      box-sizing:border-box;
      align-items:center;
      gap:10px;
      border:1px solid #cfe4df;
      background:#f6fbf9;
      color:#203431;
      border-radius:14px;
      padding:11px 12px;
      margin-bottom:9px;
      text-align:left;
      cursor:pointer;
    }
    .msc-mobile-accept-all.is-active{
      border-color:#9fd5ca;
      background:#edf9f5;
    }
    .msc-mobile-accept-all-icon{
      width:30px;
      height:30px;
      border-radius:10px;
      background:#fff;
      color:#00675f;
      display:grid;
      place-items:center;
      flex:0 0 auto;
      border:1px solid #d9e9e5;
    }
    .msc-mobile-accept-all>span:last-child{
      min-width:0;
    }
    .msc-mobile-accept-all strong,
    .msc-mobile-accept-all small{
      display:block;
    }
    .msc-mobile-accept-all strong{
      font-size:11.5px;
    }
    .msc-mobile-accept-all small{
      margin-top:2px;
      color:#758582;
      font-size:9.5px;
      font-weight:750;
    }

    .msc-consents{
      gap:7px;
    }
    .msc-consent-row{
      border:1px solid #e1e9e7;
      border-radius:14px;
      background:#fff;
      overflow:hidden;
    }
    .msc-consent-row.is-saved{
      background:#f8fbfa;
      border-color:#e0ebe8;
    }
    .msc-consent-row>label{
      border:0!important;
      border-radius:0!important;
      padding:10px 10px 7px!important;
      background:transparent!important;
      gap:9px!important;
      cursor:pointer;
    }
    .msc-consent-row.is-saved>label{
      cursor:default;
    }
    .msc-consent-row .msc-consent-status{
      width:18px;
      height:18px;
    }
    .msc-consent-row input{
      width:17px;
      height:17px;
      margin-top:1px;
    }
    .msc-consent-copy{
      min-width:0;
    }
    .msc-consent-copy strong{
      color:#263a37;
      font-size:11.2px;
    }
    .msc-consent-short{
      display:block!important;
      margin-top:2px;
      color:#778784!important;
      font-size:9.8px!important;
      line-height:1.35!important;
    }
    .msc-consent-desktop-detail{
      display:none!important;
    }
    .msc-consent-detail-toggle{
      width:max-content;
      border:0;
      background:transparent;
      color:#00675f;
      padding:0 10px 9px 36px;
      font-size:9.5px;
      font-weight:950;
      cursor:pointer;
    }
    .msc-consent-mobile-detail{
      display:block!important;
      border-top:1px solid #edf2f0;
      margin:0 10px;
      padding:9px 0 10px 26px;
      color:#6e7f7c;
      font-size:9.7px;
      line-height:1.45;
      font-weight:700;
    }

    /* Step 3 */
    .msc-mobile-payment-summary{
      width:100%;
      box-sizing:border-box;
      display:grid;
      gap:8px;
      border:1px solid #e0ebe8;
      background:#f8fbfa;
      border-radius:14px;
      padding:11px 12px;
      margin-bottom:10px;
    }
    .msc-mobile-payment-summary>div{
      display:flex;
      align-items:center;
      justify-content:space-between;
      gap:10px;
    }
    .msc-mobile-payment-summary>div span{
      min-width:0;
      color:#243a36;
      font-size:11px;
      font-weight:900;
      overflow:hidden;
      text-overflow:ellipsis;
      white-space:nowrap;
    }
    .msc-mobile-payment-summary>div strong{
      color:#172725;
      font-size:11px;
      white-space:nowrap;
    }
    .msc-mobile-payment-ok{
      display:flex;
      align-items:center;
      gap:5px;
      color:#00675f;
      font-size:9.5px;
      font-weight:900;
    }
    .msc-mobile-back-link{
      width:max-content;
      align-items:center;
      gap:5px;
      border:0;
      background:transparent;
      color:#5c706d;
      padding:2px 0 8px;
      font-size:9.8px;
      font-weight:900;
      cursor:pointer;
    }

    /* Compact Wompi payment surface for step 3 */
    .msc-payment-pane .msc-production-card{
      margin-top:5px;
      padding:0;
      border:0;
      border-radius:0;
      background:transparent;
      box-shadow:none;
      gap:12px;
    }
    .msc-payment-pane .msc-production-title-clean{
      grid-template-columns:minmax(0,1fr) auto;
      gap:10px;
      align-items:center;
      padding-bottom:10px;
    }
    .msc-payment-pane .msc-production-title strong{
      font-size:15px;
    }
    .msc-payment-pane .msc-production-title small{
      display:none;
    }
    .msc-payment-pane .msc-payment-brands{
      gap:5px;
      justify-content:flex-end;
    }
    .msc-payment-pane .msc-payment-brand{
      height:27px;
      min-width:46px;
    }
    .msc-payment-pane .msc-payment-brand-mastercard{
      min-width:69px;
      transform:scale(.94);
      transform-origin:right center;
    }
    .msc-payment-pane .msc-production-form-head{
      display:none;
    }
    .msc-payment-pane .msc-production-fields{
      grid-template-columns:repeat(6,minmax(0,1fr));
      gap:8px;
    }
    .msc-payment-pane .msc-production-fields label:first-child{
      grid-column:1/-1;
    }
    .msc-payment-pane .msc-production-wide{
      grid-column:1/-1;
    }
    .msc-payment-pane .msc-production-fields label:nth-child(3){
      grid-column:1/3;
    }
    .msc-payment-pane .msc-production-fields label:nth-child(4){
      grid-column:3/5;
    }
    .msc-payment-pane .msc-production-fields label:nth-child(5){
      grid-column:5/7;
    }
    .msc-payment-pane .msc-production-fields input{
      min-height:48px;
      border-radius:12px;
      padding:0 11px;
      font-size:13px;
    }
    .msc-payment-pane .msc-payment-breakdown{
      display:none;
    }
    .msc-payment-pane .msc-production-consent{
      grid-template-columns:auto minmax(0,1fr);
      padding:10px 11px;
      border-radius:13px;
      background:#f8fbfa;
    }
    .msc-payment-pane .msc-production-consent strong{
      font-size:10.7px;
      margin-bottom:1px;
    }
    .msc-payment-pane .msc-production-consent span{
      font-size:9.6px;
      line-height:1.35;
    }
    .msc-production-consent-desktop-text{
      display:none!important;
    }
    .msc-production-consent-mobile-text{
      display:block!important;
    }
    .msc-payment-pane .msc-production-consent a{
      display:inline-flex;
      margin-top:3px;
      font-size:9px;
    }
    .msc-payment-pane .msc-production-primary{
      min-height:52px;
      border-radius:14px;
      font-size:12px;
    }
    .msc-payment-pane .msc-production-foot{
      border:0;
      background:transparent;
      padding:2px 1px;
      justify-content:center;
      font-size:9px;
      text-align:center;
    }
    .msc-payment-pane .msc-production-state,
    .msc-payment-pane .msc-production-error,
    .msc-payment-pane .msc-production-pending,
    .msc-payment-pane .msc-production-approved{
      border-radius:13px;
      padding:11px;
    }
    .msc-payment-pane .msc-production-three-ds{
      margin-top:5px;
      border-radius:14px;
    }
    .msc-payment-pane .msc-3ds-challenge{
      height:500px;
    }

    .msc-actions{
      margin-top:13px;
      padding-top:11px;
      border-top:1px solid #edf2f0;
      display:grid;
      gap:5px;
    }
    .msc-actions button,
    .msc-actions a{
      min-height:34px;
      width:100%;
      box-sizing:border-box;
      border:0;
      background:transparent;
      color:#657875;
      font-size:9.8px;
      padding:0;
    }
  }

  @media(max-width:390px){
    .msc-card,
    .msc-checkout-card,
    .msc-summary-card{
      padding:14px;
    }
    .msc-mobile-progress h2{
      font-size:21px;
    }
    .msc-mobile-wizard-actions-split{
      grid-template-columns:1fr;
    }
    .msc-mobile-secondary{
      order:2;
      min-height:42px;
    }
    .msc-production-diagnostic{
      grid-template-columns:1fr;
    }
    .msc-payment-pane .msc-production-fields{
      gap:7px;
    }
    .msc-payment-pane .msc-production-fields input{
      padding:0 9px;
      font-size:12px;
    }
  }

`;
